// ---------------------------------------------------------------------------
// DnD Email Editor V2 – Add-columns modal
//
// Shown when a Columns block is first added (dragged in or clicked from the
// palette). Prompts the user to choose the column layout ("how many columns")
// with the same visual thumbnails the inspector uses. Confirming inserts a
// columns layer with the chosen layout; Cancel / Esc / backdrop aborts the add.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { ColumnsLayout } from './dnd-model';
import { ColumnLayoutPicker } from './dnd-column-layouts';

export interface DndColumnsModalProps {
  isOpen: boolean;
  /** Layout to preselect — '2' when adding, the layer's current layout when editing. */
  initialLayout?: ColumnsLayout;
  /** Confirm-button label — "Add Columns" on add, "Apply Layout" on edit. */
  confirmLabel?: string;
  /** Insert/update a columns layer with the chosen layout, then close. */
  onConfirm: (layout: ColumnsLayout) => void;
  /** Discard (no block added / layout unchanged) and close. */
  onClose: () => void;
}

export function DndColumnsModal({
  isOpen,
  initialLayout = '2',
  confirmLabel = 'Add Columns',
  onConfirm,
  onClose,
}: DndColumnsModalProps) {
  const [layout, setLayout] = useState<ColumnsLayout>(initialLayout);

  // Seed the selection from the current/default layout each time it opens.
  useEffect(() => {
    if (isOpen) setLayout(initialLayout);
  }, [isOpen, initialLayout]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="dnd-html-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="dnd-html-modal dnd-simple-modal"
        role="dialog"
        aria-labelledby="dnd-columns-modal-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="dnd-html-modal-header">
          <h3 id="dnd-columns-modal-title">Choose a column layout</h3>
          <button type="button" className="dnd-html-modal-close" title="Close" aria-label="Close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="dnd-simple-modal-body">
          <p className="dnd-simple-modal-hint">
            Pick how many columns and their proportions. You can restyle or change this later in the inspector.
          </p>
          <div className="dnd-columns-modal-picker">
            <ColumnLayoutPicker value={layout} onSelect={setLayout} />
          </div>
        </div>

        <div className="dnd-html-modal-footer">
          <button type="button" className="dnd-html-modal-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="dnd-html-modal-btn dnd-html-modal-btn-primary" onClick={() => onConfirm(layout)}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
